import React, { useEffect, useRef, useState } from "react";
import { withPrefix } from "gatsby";
import classNames from "classnames";
import Modal from "./modal";

const sitePrefix = /\/?([^/]+)\/?/.exec(withPrefix("/"))?.[1];
const countryMap = {
  IT: "it_IT",
  FR: "fr_FR",
  ES: "es_ES",
  DE: "de_DE",
  CZ: "cs_CZ",
  PL: "pl_PL",
  IE: "en_IE",
  GB: "en_GB",
  PT: "pt_PT",
  NL: "nl_NL",
  NO: "no_NO",
  HR: "hr_HR",
  RO: "ro_RO",
  BE: ["nl_BE", "fr_BE"],
  GR: "el_GR",
  RS: "sr_RS_latin",
  HU: "hu_HU",
  UA: "uk_UA",
  FI: "fi_FI",
  CH: ["de_CH", "it_CH", "fr_CH"],
  SE: "sv_SE",
  DK: "da_DK",
  BG: "bg_BG",
  MA: "fr_MA",
  SK: "sk_SK",
  EE: "et_EE",
  LV: "lv_LV",
  LT: "lt_LT",
  YY: "xx_YY",
  ZZ: "cc_ZZ",
  SI: "sl_SI",
  TR: "tr_TR",
};
const languageMap = {
  "bg": "bg_BG",
  "cs": "cs_CZ",
  "da": "da_DK",
  "de": "de_DE",
  "de-ch": "de_CH",
  "el": "el_GR",
  "es": "es_ES",
  "en": "en_GB",
  "en-ie": "en_IE",
  "et": "et_EE",
  "fi": "fi_FI",
  "fr": "fr_FR",
  "fr-ch": "fr_CH",
  "fr-be": "fr_BE",
  "fr-ma": "fr_MA",
  "hr": "hr_HR",
  "hu": "hu_HU",
  "it": "it_IT",
  "it-ch": "it_CH",
  "lt": "lt_LT",
  "lv": "lv_LV",
  "nl": "nl_NL",
  "nl-be": "nl_BE",
  "no": "no_NO",
  "pl": "pl_PL",
  "pt": "pt_PT",
  "ro": "ro_RO",
  "ru": "ru_RU",
  "sk": "sk_SK",
  "sr": "sr_RS_latin",
  "sv": "sv_SE",
  "tr": "tr_TR",
  "uk": "uk_UA",
};

const geolocate = async () => {
  const response = await fetch("/headless-api/geolocation");
  if (!response.ok) {
    throw new Error(``);
  }
  const data = await response.json();
  return data?.headers || {};
};

const getLanguageSuggestion = async () => {
  // Recupero le info di geolocalizzazione da api
  const geoHeaders = await geolocate();
  const geoCountry = geoHeaders["CloudFront-Viewer-Country"];
  // Recupero il/i siti corrispondenti alla country
  const geoSite = geoCountry ? countryMap[geoCountry] : [];
  const geoSites = Array.isArray(geoSite) ? geoSite : [geoSite];
  // Il sito suggerito può dipendere anche dalla lingua del browser
  const language = navigator.language.toLowerCase();
  const suggestedLangPrefix =
    geoSites.length < 2
      ? geoSites[0]
      : geoSites.find(
          (item) =>
            item.replace("_", "-").toLowerCase() === language ||
            item.slice(0, 2).toLowerCase() === language.slice(0, 2)
        ) || (geoSites.includes(sitePrefix) ? sitePrefix : geoSites[0]);
  // Se non riesco a trovare un suggerimento diverso dall'attuale, non fare nulla
  if (!suggestedLangPrefix || !sitePrefix || suggestedLangPrefix === sitePrefix) return null;
  const supportedLanguages = Object.keys(languageMap);
  // Cerco la lingua per le label, se non c'è tra quelle suportate vado su en-GB
  const langCode = supportedLanguages.includes(language)
    ? languageMap[language].slice(0, 5).replace("_", "-")
    : supportedLanguages.includes(language.slice(0, 2))
    ? languageMap[language.slice(0, 2)].slice(0, 5).replace("_", "-")
    : "en-GB";
  // Recupero i
  const hreflangs = [...document.querySelectorAll('link[rel="alternate"][hreflang][href]')];
  const hreflang = suggestedLangPrefix.slice(0, 5).replace("_", "-").toLowerCase();
  const hreflangItem = hreflangs.find((item) => item.getAttribute("hreflang") === hreflang);
  return {
    langCode,
    langPrefix: suggestedLangPrefix,
    href: hreflangItem
      ? hreflangItem.getAttribute("href")
      : `${window.location.origin}/${suggestedLangPrefix}/`,
  };
};

const onKeyDown = () => void 0;

const LanguageModal = () => {
  const ref = useRef(null);
  const [langSuggestion, setLangSuggestion] = useState(null);

  useEffect(() => {
    const ignoreVal = localStorage.getItem("haier-ignore-language");
    if (ignoreVal !== "true") {
      const loadLanguageSuggestion = async () => {
        try {
          const suggestion = await getLanguageSuggestion();
          if (suggestion) {
            console.log("suggestion: ", suggestion);
            const langLabel = "site_" + suggestion.langPrefix.toLowerCase();
            const siteLabel = "site_" + sitePrefix.toLowerCase();
            const params = new URLSearchParams({ lang: suggestion.langCode });
            params.append("labels", "langNavSuggestion");
            params.append("labels", "langNavIgnore");
            params.append("labels", "langNavAccept");
            params.append("labels", "langNavTitle");
            params.append("labels", langLabel);
            params.append("labels", siteLabel);
            const response = await fetch("/headless-api/labels?" + params.toString());
            if (!response.ok) {
              throw new Error(`Error loading labels ${response.status} ${response.statusText}`);
            }
            const data = await response.json();
            if (
              data.labels?.langNavIgnore &&
              data.labels?.langNavAccept &&
              data.labels?.langNavTitle &&
              data.labels?.[langLabel] &&
              data.labels?.[siteLabel]
            ) {
              setLangSuggestion({
                ...suggestion,
                labels: {
                  ...data.labels,
                  siteLabel: data.labels[siteLabel],
                  langLabel: data.labels[langLabel],
                },
              });
            } else {
              console.warn(
                `Missing some needed translations for lang ${suggestion.langCode}`,
                data.labels
              );
            }
          }
        } catch (err) {
          console.error("Error loading suggestion: ", err);
        }
      };
      loadLanguageSuggestion();
    }
  }, []);

  const ignoreWarning = () => {
    localStorage.setItem("haier-ignore-language", "true");
    setLangSuggestion(null);
  };
  const show = !!langSuggestion;

  return (
    <Modal>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={ref}
        className={classNames("language-modal", { show })}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={show ? 0 : -1}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        role="dialog"
        aria-hidden={!show}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        onKeyDown={onKeyDown}
      >
        <div className="language-modal__dialog" role="document">
          {langSuggestion && (
            <div className="language-modal__container">
              <div className="language-modal__title">
                {langSuggestion.labels.langNavTitle
                  .replace("{BRAND}", "Haier")
                  .replace("{COUNTRY}", langSuggestion.labels.langLabel)}
              </div>
              {langSuggestion?.labels?.langNavSuggestion ? (
                <div className="language-modal__abstract">
                  {langSuggestion.labels.langNavSuggestion
                    .replace("{BRAND}", "Haier")
                    .replace("{COUNTRY}", langSuggestion.labels.langLabel)}
                </div>
              ) : (
                <div className="language-modal__abstract"></div>
              )}

              <div className="language-modal__buttons">
                <button
                  className="language-modal__close btn-link btn-link--white"
                  onClick={ignoreWarning}
                >
                  {langSuggestion.labels.langNavIgnore
                    .replace("{BRAND}", "Haier")
                    .replace("{COUNTRY}", langSuggestion.labels.siteLabel)}
                </button>

                <a
                  href={langSuggestion.href}
                  rel="noreferrer"
                  className="language-modal__stay btn-link btn-link--blue"
                  target="_blank"
                >
                  {langSuggestion.labels.langNavAccept
                    .replace("{BRAND}", "Haier")
                    .replace("{COUNTRY}", langSuggestion.labels.langLabel)}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default LanguageModal;
